// Pro's Templates page, composed here because it joins two features (spec
// ui-perf/ui-switch-classic-simple-pro, PR e2): Pro's list (`features/studio2`) with the templates
// area's own preview, form and runs (`features/templates`), unchanged. A feature never imports
// another; the composition root may.
import { useQueryClient } from "@tanstack/react-query";
import { createRoute, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";

import {
  getListJobsQueryKey,
  useGetTemplate,
} from "../api/generated/endpoints/generation/generation";
import type { TemplateCard, TemplateDetail } from "../api/generated/model";
import { freshUrls } from "../api/signed-urls";
import { studio2Route } from "../features/studio2/routes";
import { TemplatesPage } from "../features/studio2/templates/TemplatesPage";
import { TemplateDialog } from "../features/templates/TemplateDialog";
import { TemplateGenerations } from "../features/templates/TemplateGenerations";
import { TemplatePanel } from "../features/templates/TemplatePanel";

const ROUTE = "/signed-in/teams/$teamId/brands/$brandId/studio/pro/templates";

// `?tab=my-generations`: the runs. `?template=<slug>`: a template's preview (Pro Home's cards link
// here), the Templates page's own rule for a slug; anything else is dropped.
const templatesSearch = z.object({
  tab: z.literal("my-generations").optional().catch(undefined),
  template: z
    .string()
    .max(64)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
    .optional()
    .catch(undefined),
});

export const studio2TemplatesRoute = createRoute({
  getParentRoute: () => studio2Route,
  path: "templates",
  validateSearch: (search) => templatesSearch.parse(search),
  component: ProTemplates,
});

function ProTemplates() {
  const { teamId, brandId } = useParams({ from: ROUTE });
  // Another brand starts the page anew: no preview or form of the last brand's (decision 31 of
  // spec ui-perf/pro-studio-redesign).
  return <BrandTemplates key={`${teamId} ${brandId}`} teamId={teamId} brandId={brandId} />;
}

/** One brand's templates in Pro: the list, a template's preview, its form and the runs. */
function BrandTemplates({ teamId, brandId }: { teamId: string; brandId: string }) {
  const search = useSearch({ from: ROUTE });
  const navigate = useNavigate({ from: "/teams/$teamId/brands/$brandId/pro/templates" });
  const queryClient = useQueryClient();
  const [previewed, setPreviewed] = useState<TemplateCard | null>(null);
  const [selected, setSelected] = useState<{ template: TemplateCard; replicate: boolean } | null>(
    null,
  );
  const linked = search.template;
  // The linked template's detail, which carries every card field; its examples are signed.
  const link = useGetTemplate(teamId, linked ?? "", {
    query: {
      enabled: linked !== undefined,
      ...freshUrls<TemplateDetail>((detail) => detail.urls_expire_at),
    },
  });
  // A coming soon template opens nothing, as its card: its link is dropped (the #1459 rule).
  const dropped = linked !== undefined && (link.data?.coming_soon === true || link.isError);
  const dropLink = () => void navigate({ search: { tab: search.tab }, replace: true });
  useEffect(() => {
    if (dropped) void navigate({ search: { tab: search.tab }, replace: true });
  }, [dropped, navigate, search.tab]);
  const opened = linked !== undefined && link.data !== undefined && !link.data.coming_soon;
  const shown = previewed ?? (opened ? (link.data ?? null) : null);
  const closePreview = () => {
    setPreviewed(null);
    if (linked !== undefined) dropLink();
  };
  return (
    <>
      <TemplatesPage
        onOpen={setPreviewed}
        generations={<TemplateGenerations teamId={teamId} brandId={brandId} />}
        panel={
          selected === null ? null : (
            <TemplatePanel
              key={`${selected.template.id}:${String(selected.replicate)}`}
              teamId={teamId}
              brandId={brandId}
              template={selected.template}
              replicate={selected.replicate}
              onClose={() => setSelected(null)}
              onStarted={() => {
                void queryClient.invalidateQueries({ queryKey: getListJobsQueryKey(teamId) });
                // As the Templates page: a run started, My generations shows; the form stays.
                void navigate({ search: { tab: "my-generations" }, replace: true });
              }}
            />
          )
        }
      />
      <TemplateDialog
        teamId={teamId}
        template={shown}
        onPreview={setPreviewed}
        onSelect={(template, replicate) => {
          closePreview();
          setSelected({ template, replicate });
        }}
        onClose={closePreview}
      />
    </>
  );
}
